import {gql} from '@apollo/client';
import produce from 'immer';
import React from 'react';

import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {ProcessTaskDefinitionVersionNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {readQuerySafe, writeFragmentsToCache, writeQuery} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';
import {ProcessFlowchartConfigVersionSpec, TaskSchemaSpec} from 'processes/dao/ProcessSchemaDAO';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import * as ProcessTaskDefinitionDAO from 'processes/dao/ProcessTaskDefinitionDAO';
import {AllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/__generated/AllProcessTaskSchemasForSchemaConfigQuery';
import {ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY} from 'processes/modals/utils/ProcessesAllSchemasQueryUtils';
import {AllConfigurableTaskDefinitionsPaginatedQuery} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/__generated/AllConfigurableTaskDefinitionsPaginatedQuery';
import {
  ALL_CONFIGURABLE_TASK_DEFINITIONS_PAGINATED_QUERY,
  ConfigurableTaskDefinitionGQL,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {createSnapshotField, updateSnapshotField} from 'schemas/snapshot-field/SnapshotFieldDAO';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {withLoadingScreen} from 'util/Promises';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {
  FetchTaskDefinitionVersionQuery,
  FetchTaskDefinitionVersionQueryVariables,
} from './__generated/FetchTaskDefinitionVersionQuery';
import {MinimalProcessTaskSchemaEditorQuery} from './__generated/MinimalProcessTaskSchemaEditorQuery';
import {ProcessTaskSchemaEditorQuery} from './__generated/ProcessTaskSchemaEditorQuery';

type ProcessTaskSchemaGQL =
  AllProcessTaskSchemasForSchemaConfigQuery['processTaskSchemasSupportingPlatformFeature'][number];

export type MinimalProcessTaskSchema = ExtractByTypename<
  MinimalProcessTaskSchemaEditorQuery['processTaskSchema'],
  'ProcessTaskSchemaNode'
>;

export type ProcessTaskSchema = MinimalProcessTaskSchema & {
  flowchartConfig:
    | ExtractByTypename<
        ProcessTaskSchemaEditorQuery['processTaskSchema'],
        'ProcessTaskSchemaNode'
      >['flowchartConfig']
    | null;
};

export function isEmptyOrWhitespace(str: string): RegExpMatchArray | null {
  return str.match(/^\s*$/g);
}

export function insertOrReplaceTaskSchema<
  TTaskSchema extends {__typename: 'ProcessTaskSchemaNode'; id: string},
>(taskSchemas: Array<TTaskSchema>, taskSchemaToInsert: TTaskSchema): Array<TTaskSchema> {
  const [array, _] = produce(
    [taskSchemas, taskSchemaToInsert],
    ([draftTaskSchemas, draftTaskSchemaToInsert]) => {
      const schemaIdx = draftTaskSchemas.findIndex(({id}) => taskSchemaToInsert.id === id);
      if (schemaIdx < 0) {
        draftTaskSchemas.push(draftTaskSchemaToInsert);
      } else {
        draftTaskSchemas[schemaIdx] = draftTaskSchemaToInsert;
      }
    }
  );
  return array;
}

export interface UpdateProcessSchema {
  taskGroupPrefix?: string;
  name?: string;
  sqlIdentifier?: string;
  taskSchemaPrefix?: string;
  outputSchemaPrefix?: string;
  taskFields?: Array<ClientSchemaField>;
  outputFields?: Array<ClientSchemaField>;
  autopopulateDirectOutputsFromTasks?: boolean;
  defaultResponsibleParties?: Array<MinimalEntity>;
  responsiblePartyLookupConfig?: LookupConfigSpec | null;
  taskFinderLookupConfig?: LookupConfigSpec | null;
  updateTaskFinder?: boolean;
  canSetAssigneeOnTaskCreation?: boolean;
  canOnlyBePartOfFlowchart?: boolean;
  defaultCreationFolderId?: string | null;
  defaultEntryExecutionFolderId?: string | null;
  taskGroupInstructions?: string | null;
  isPropagateWatchersEnabled?: boolean;
  flowchartConfig?: ProcessFlowchartConfigVersionSpec | null;
  configEditorEventGroupId?: string | null;
  didInitialFlowchartHaveChainedRouters?: boolean;
}

export type FieldsKey = 'fields' | 'taskFields' | 'outputFields';

export async function createOrUpdateSnapshotField(
  changedFields: Array<ClientSchemaField | SchemaField>,
  newFields: Array<ClientSchemaField>,
  fieldsKey: FieldsKey,
  setErrors: (errors: Record<string, string>) => void,
  setStateOnUpdate?: (params: UpdateProcessSchema & {updateTables?: boolean}) => void,
  refetchProcessSchema?: () => void,
  processTaskSchema?: ProcessTaskSchema | null
): Promise<void> {
  if (setStateOnUpdate) {
    setStateOnUpdate({
      [fieldsKey]: newFields,
    } as UpdateProcessSchema);
  }
  assert(changedFields.length >= 1, 'Expected at least one created/updated snapshot field');
  const schemaId = fieldsKey === 'taskFields' ? processTaskSchema!.id : processTaskSchema!.outputSchema!.id;
  const saveSnapshotFields = async (): Promise<void> => {
    try {
      for (const snapshotField of changedFields) {
        if (!snapshotField.apiId) {
          await createSnapshotField({
            schemaId,
            displayName: snapshotField.displayName,
            systemName: snapshotField.systemName,
            tooltipText: snapshotField.tooltipText,
            lookupConfigSpec: snapshotField.lookupConfig!,
          });
        } else {
          await updateSnapshotField({
            snapshotFieldId: snapshotField.apiId,
            displayName: snapshotField.displayName,
            systemName: snapshotField.systemName,
            tooltipText: snapshotField.tooltipText,
            lookupConfigSpec: snapshotField.lookupConfig!,
            archiveRecord: snapshotField.archiveRecord,
          });
        }
      }
      if (refetchProcessSchema != null) {
        refetchProcessSchema();
      }
      if (setStateOnUpdate) {
        setStateOnUpdate({
          taskFields: processTaskSchema!.allFields,
          outputFields: processTaskSchema!.outputSchema?.allFields ?? null,
        } as UpdateProcessSchema);
      }
      notifications.success('Updated schema successfully');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON != null) {
        setErrors(e.responseJSON.errors ?? {});
      }
      notifications.notifyError(e);
    }
  };

  await withLoadingScreen(saveSnapshotFields());
}

const SAVING_TASK_SCHEMA_MESSAGE = 'Saving...';

/**
 * Builds the task schema specification object from update parameters.
 */
function buildTaskSchemaSpec(params: UpdateProcessSchema): TaskSchemaSpec {
  const hasOutputSchemaUpdates =
    params.outputSchemaPrefix != null ||
    params.outputFields != null ||
    params.updateTaskFinder ||
    params.autopopulateDirectOutputsFromTasks != null;

  return {
    ...(params.taskGroupPrefix != null ? {taskGroupPrefix: params.taskGroupPrefix} : {}),
    ...(params.name != null ? {name: params.name} : {}),
    ...(params.sqlIdentifier != null ? {sqlIdentifier: params.sqlIdentifier} : {}),
    ...(params.taskSchemaPrefix != null ? {prefix: params.taskSchemaPrefix} : {}),
    ...(params.canSetAssigneeOnTaskCreation != null
      ? {canSetAssigneeOnTaskCreation: params.canSetAssigneeOnTaskCreation}
      : {}),
    ...(params.canOnlyBePartOfFlowchart != null
      ? {canOnlyBePartOfFlowchart: params.canOnlyBePartOfFlowchart}
      : {}),
    defaultResponsiblePartiesIds:
      params.defaultResponsibleParties === undefined
        ? undefined
        : params.defaultResponsibleParties.map(({id}) => id),
    responsiblePartyLookupConfigs:
      params.responsiblePartyLookupConfig === undefined
        ? undefined
        : params.responsiblePartyLookupConfig == null
          ? null
          : [params.responsiblePartyLookupConfig],
    ...(params.taskFields != null ? {schemaFields: massageFieldsForSubmit(params.taskFields)} : {}),
    processOutputSchema: hasOutputSchemaUpdates
      ? {
          ...(params.outputSchemaPrefix != null ? {prefix: params.outputSchemaPrefix} : {}),
          ...(params.outputFields != null ? {schemaFields: massageFieldsForSubmit(params.outputFields)} : {}),
          ...(params.updateTaskFinder ? {taskFinderLookupConfig: params.taskFinderLookupConfig} : {}),
          ...(params.autopopulateDirectOutputsFromTasks != null
            ? {autopopulateFromTasks: params.autopopulateDirectOutputsFromTasks}
            : {}),
        }
      : {},
    defaultCreationFolderId: params.defaultCreationFolderId,
    defaultEntryExecutionFolderId: params.defaultEntryExecutionFolderId,
    ...(params.flowchartConfig != null ? {flowchartConfig: params.flowchartConfig} : {}),
    taskGroupInstructions: params.taskGroupInstructions,
    ...(params.isPropagateWatchersEnabled != null
      ? {isPropagateWatchersEnabled: params.isPropagateWatchersEnabled}
      : {}),
    configEditorEventGroupId: params.configEditorEventGroupId,
    didInitialFlowchartHaveChainedRouters: params.didInitialFlowchartHaveChainedRouters ?? false,
    actionOrigin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE,
  };
}

/**
 * Shows a notification for a schema update error, extracting field-level
 * error messages from responseJSON when available.
 */
// oxlint-disable-next-line no-explicit-any
export function notifySchemaUpdateError(errorObject: any): void {
  if (errorObject?.responseJSON != null) {
    const errors: Record<string, string> = errorObject.responseJSON.errors ?? {};
    const errorMessages: Array<string> = Object.values(errors);
    if (errorMessages.length > 0) {
      const friendlyMessage = notifications.getFriendlyErrorAsElement(errorObject, '');
      notifications.error(
        <>
          {friendlyMessage || 'Your request could not be completed.'}
          <br />
          {errorMessages.map((msg, idx) => (
            <React.Fragment key={idx}>
              {msg}
              {idx < errorMessages.length - 1 && <br />}
            </React.Fragment>
          ))}
        </>
      );
      return;
    }
  }
  notifications.notifyError(errorObject);
}

/**
 * Handles errors from task schema update operations.
 */
function handleUpdateError(
  // oxlint-disable-next-line no-explicit-any
  errorObject: any,
  params: UpdateProcessSchema,
  setStateOnUpdate: (params: UpdateProcessSchema & {updateTables?: boolean}) => void,
  setErrors: (errors: Record<string, string>) => void,
  oldProcessTaskSchema: ProcessTaskSchema,
  setIsEditingFlowchart?: (isEditing: boolean) => void
): void {
  if (errorObject?.responseJSON != null) {
    setErrors(errorObject.responseJSON.errors ?? {});
  }
  notifySchemaUpdateError(errorObject);
  // Revert field state to match server on failure
  setStateOnUpdate({
    taskFields: oldProcessTaskSchema.allFields,
    outputFields: oldProcessTaskSchema.outputSchema?.allFields ?? null,
    updateTables: true,
  });
  // If the flowchart config was being updated we should put it back into editing mode.
  if (params.flowchartConfig != null && setIsEditingFlowchart != null) {
    setIsEditingFlowchart(true);
  }
}

/**
 * Updates a process task schema directly (without creating a task definition version).
 */
export async function updateProcessTaskSchema(
  params: UpdateProcessSchema,
  setStateOnUpdate: (params: UpdateProcessSchema & {updateTables?: boolean}) => void,
  setErrors: (errors: Record<string, string>) => void,
  oldProcessTaskSchema: ProcessTaskSchema | null,
  hasOutputSchema: boolean,
  afterUpdate?: (newTaskSchema: ProcessTaskSchemaGraphQL) => void,
  setIsEditingFlowchart?: (isEditing: boolean) => void
): Promise<ProcessTaskSchemaGraphQL | null> {
  // Optimistically update component state
  setStateOnUpdate(params);
  setErrors({});
  if (oldProcessTaskSchema == null) {
    return null;
  }
  try {
    const taskSchemaSpec = buildTaskSchemaSpec(params);

    const taskSchema = await withLoadingScreen(
      ProcessSchemaDAO.updateProcessTaskSchema({schemaId: oldProcessTaskSchema.id, ...taskSchemaSpec}),
      SAVING_TASK_SCHEMA_MESSAGE
    );
    cacheUpdatedTaskSchema(taskSchema);

    // Update state with field table from server on success
    setStateOnUpdate({
      taskFields: taskSchema.allFields,
      outputFields: taskSchema.outputSchema?.allFields ?? null,
    } as UpdateProcessSchema);
    if (afterUpdate != null) {
      afterUpdate(taskSchema);
    }
    notifications.success('Updated schema successfully');
    return taskSchema;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    handleUpdateError(e, params, setStateOnUpdate, setErrors, oldProcessTaskSchema, setIsEditingFlowchart);
    return null;
  }
}

/**
 * Updates a process task schema by creating a new task definition version.
 */
export async function updateProcessTaskSchemaForTaskDefinitionVersion(
  params: UpdateProcessSchema,
  taskDefinitionId: string,
  setStateOnUpdate: (params: UpdateProcessSchema & {updateTables?: boolean}) => void,
  setErrors: (errors: Record<string, string>) => void,
  oldProcessTaskSchema: ProcessTaskSchema | null,
  hasOutputSchema: boolean,
  afterUpdate?: (
    newTaskSchema: ProcessTaskSchemaGraphQL,
    newTaskDefinitionVersion: ProcessTaskDefinitionVersionNode
  ) => void,
  setIsEditingFlowchart?: (isEditing: boolean) => void
): Promise<ProcessTaskDefinitionVersionNode | null> {
  // Optimistically update component state
  setStateOnUpdate(params);
  setErrors({});
  if (oldProcessTaskSchema == null) {
    return null;
  }
  try {
    const taskSchemaSpec = buildTaskSchemaSpec(params);

    const taskDefinitionVersion = await withLoadingScreen(
      ProcessTaskDefinitionDAO.updateProcessTaskSchemaWithTaskDefinitionVersion({
        processTaskDefinitionId: taskDefinitionId,
        ...taskSchemaSpec,
      }),
      SAVING_TASK_SCHEMA_MESSAGE
    );
    const maybeTaskSchema = taskDefinitionVersion.processTaskSchema;
    if (isInaccessible(maybeTaskSchema)) {
      throw new Error('Task schema is inaccessible');
    }
    const taskSchema = maybeTaskSchema as ProcessTaskSchemaGraphQL;
    cacheUpdatedTaskDefinitionVersion(taskDefinitionVersion, taskDefinitionId);
    cacheUpdatedTaskSchema(taskSchema);

    setStateOnUpdate({
      taskFields: taskSchema.allFields,
      outputFields: taskSchema.outputSchema?.allFields ?? null,
    } as UpdateProcessSchema);
    if (afterUpdate != null) {
      afterUpdate(taskSchema, taskDefinitionVersion);
    }
    notifications.success('Updated schema successfully');
    return taskDefinitionVersion;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    handleUpdateError(e, params, setStateOnUpdate, setErrors, oldProcessTaskSchema, setIsEditingFlowchart);
    return null;
  }
}

export function cacheUpdatedTaskSchema(updatedTaskSchema: ProcessTaskSchemaGQL): void {
  const processTaskSchemaResult = readQuerySafe<AllProcessTaskSchemasForSchemaConfigQuery>({
    query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY,
  });
  if (processTaskSchemaResult != null) {
    const taskSchemasWithUpdate = insertOrReplaceTaskSchema(
      processTaskSchemaResult.processTaskSchemasSupportingPlatformFeature,
      updatedTaskSchema
    );
    writeQuery(
      {query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY},
      {
        ...processTaskSchemaResult,
        processTaskSchemasSupportingPlatformFeature: taskSchemasWithUpdate,
        __typename: 'Query',
      }
    );
    writeFragmentsToCache(getFragmentWrapper('AllProcessTaskSchemasForSchemaConfig'), [
      {processTaskSchemasSupportingPlatformFeature: taskSchemasWithUpdate, __typename: 'Query'},
    ]);
  }

  writeFragmentsToCache(getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig'), [updatedTaskSchema]);
}

/**
 * Updates the task definition cache with a new task definition version when one is created.
 */
export function cacheUpdatedTaskDefinitionVersion(
  updatedTaskDefinitionVersion: ProcessTaskDefinitionVersionNode,
  taskDefinitionId: string
): void {
  const taskDefinitionsResult = readQuerySafe<AllConfigurableTaskDefinitionsPaginatedQuery>({
    query: ALL_CONFIGURABLE_TASK_DEFINITIONS_PAGINATED_QUERY,
  });
  if (taskDefinitionsResult != null) {
    const listing = taskDefinitionsResult.processTaskDefinitionsSupportingPlatformFeatureListing;
    const newTaskDefinitions = produce(listing.processTaskDefinitions, (draft) => {
      for (const taskDefinition of draft) {
        if (taskDefinition.id === taskDefinitionId) {
          taskDefinition.latestVersion = updatedTaskDefinitionVersion;
          break;
        }
      }
    });
    writeQuery(
      {query: ALL_CONFIGURABLE_TASK_DEFINITIONS_PAGINATED_QUERY},
      {
        ...taskDefinitionsResult,
        processTaskDefinitionsSupportingPlatformFeatureListing: {
          ...listing,
          processTaskDefinitions: newTaskDefinitions,
        },
        __typename: 'Query',
      },
      {limitOffsetInput: {limit: 0, offset: 0}}
    );
    writeFragmentsToCache(getFragmentWrapper('ProcessTaskDefinition'), newTaskDefinitions);
  }

  writeFragmentsToCache(getFragmentWrapper('ProcessTaskDefinitionVersion'), [updatedTaskDefinitionVersion]);
}

const FETCH_TASK_DEFINITION_VERSION_QUERY = gql`
  query FetchTaskDefinitionVersionQuery($taskDefinitionVersionId: StrictString!)
  @engComponent(name: WORKFLOWS)
  @skipAudit {
    processTaskDefinitionVersion(id: $taskDefinitionVersionId) {
      ... on ProcessTaskDefinitionVersionNode {
        ...ProcessTaskDefinitionVersionGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskDefinitionVersion').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export async function fetchTaskDefinitionVersion(
  taskDefinitionVersionId: string
): Promise<ProcessTaskDefinitionVersionNode | null> {
  const client = getApolloClient();
  try {
    const result = await client.query<
      FetchTaskDefinitionVersionQuery,
      FetchTaskDefinitionVersionQueryVariables
    >({
      query: FETCH_TASK_DEFINITION_VERSION_QUERY,
      variables: {taskDefinitionVersionId},
      fetchPolicy: 'network-only',
    });
    const taskDefinitionVersion = result.data?.processTaskDefinitionVersion;
    if (taskDefinitionVersion == null || isInaccessible(taskDefinitionVersion)) {
      return null;
    }
    return taskDefinitionVersion as ProcessTaskDefinitionVersionNode;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.error('Failed to load task definition version. Please try again.');
    return null;
  }
}

function insertSortedByName<T>(list: ReadonlyArray<T>, item: T, getName: (t: T) => string): Array<T> {
  const name = getName(item);
  const result = [...list];
  const insertIndex = result.findIndex((existing) => getName(existing).localeCompare(name) > 0);
  if (insertIndex === -1) {
    result.push(item);
  } else {
    result.splice(insertIndex, 0, item);
  }
  return result;
}

export function cacheCreatedTaskDefinition(newTaskDefinition: ConfigurableTaskDefinitionGQL): void {
  const taskDefinitionsResult = readQuerySafe<AllConfigurableTaskDefinitionsPaginatedQuery>({
    query: ALL_CONFIGURABLE_TASK_DEFINITIONS_PAGINATED_QUERY,
  });
  const taskSchemasResult = readQuerySafe<AllProcessTaskSchemasForSchemaConfigQuery>({
    query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY,
  });
  if (taskDefinitionsResult != null) {
    const listing = taskDefinitionsResult.processTaskDefinitionsSupportingPlatformFeatureListing;
    const taskDefinitionsWithUpdate = insertSortedByName(
      listing.processTaskDefinitions,
      newTaskDefinition,
      (def) => {
        if (def.latestVersion == null) {
          return '';
        }
        const schema = def.latestVersion.processTaskSchema;
        return isInaccessible(schema) ? '' : schema.name;
      }
    );
    writeQuery(
      {query: ALL_CONFIGURABLE_TASK_DEFINITIONS_PAGINATED_QUERY},
      {
        ...taskDefinitionsResult,
        processTaskDefinitionsSupportingPlatformFeatureListing: {
          ...listing,
          totalCount: listing.totalCount + 1,
          processTaskDefinitions: taskDefinitionsWithUpdate,
        },
        __typename: 'Query',
      },
      {limitOffsetInput: {limit: 0, offset: 0}}
    );
  }

  if (taskSchemasResult != null) {
    const newTaskSchema = newTaskDefinition.latestVersion.processTaskSchema;
    const taskSchemasWithUpdate = insertSortedByName(
      taskSchemasResult.processTaskSchemasSupportingPlatformFeature,
      newTaskSchema,
      (schema) => schema.name
    );
    writeQuery(
      {query: ALL_PROCESS_TASK_SCHEMAS_FOR_SCHEMA_CONFIG_QUERY},
      {
        ...taskSchemasResult,
        processTaskSchemasSupportingPlatformFeature: taskSchemasWithUpdate,
        __typename: 'Query',
      }
    );
  }

  writeFragmentsToCache(getFragmentWrapper('ProcessTaskDefinition'), [newTaskDefinition]);
}
